import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useAppData } from "@/contexts/AppDataContext";
import { canViewCallQueueInsight } from "@/utils/userUtils";
import React from "react";
import CallOverviewSection from "./sections/CallOverviewSection";
import InstantConnectInsightSection from "./sections/InstantConnectInsightSection";
import TrendsSection from "./sections/TrendsSection";

interface CallsTabProps {
  isGeneralMode: boolean;
}

const CallsTab: React.FC<CallsTabProps> = ({ isGeneralMode }) => {
  const overviewRef = React.useRef<HTMLDivElement>(null);
  const agentRef = React.useRef<HTMLDivElement>(null);
  const trendsRef = React.useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = React.useState<"overview" | "agent" | "trends">("overview");
  const [overviewOpen, setOverviewOpen] = React.useState<string | undefined>("call-detail");
  const [agentOpen, setAgentOpen] = React.useState<string | undefined>("call-detail");
  const [trendsOpen, setTrendsOpen] = React.useState<string | undefined>("call-detail");
  const suppressIoUntilRef = React.useRef<number>(0);
  const { queuesResponse, userRole } = useAppData();
  const canViewCallQueue = canViewCallQueueInsight(userRole, queuesResponse);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    const container = document.querySelector('.overflow-auto') as HTMLElement | null;
    if (!ref.current) return;
    if (container) {
      const containerTop = container.getBoundingClientRect().top;
      const targetTop = ref.current.getBoundingClientRect().top;
      const current = container.scrollTop;
      const offset = 8; // small spacing from top
      const top = current + (targetTop - containerTop) - offset;
      container.scrollTo({ top, behavior: 'smooth' });
    } else {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  React.useEffect(() => {
    const container = document.querySelector('.overflow-auto');
    if (!container) return;

    let timeoutId: NodeJS.Timeout;
    const options = { root: container as Element, threshold: 0.3, rootMargin: '-20% 0px -20% 0px' };

    const handler: IntersectionObserverCallback = (entries) => {
      // Clear existing timeout to debounce rapid changes
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (Date.now() < suppressIoUntilRef.current) return;
        let bestEntry: IntersectionObserverEntry | null = null;
        let maxRatio = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            bestEntry = entry;
          }
        });

        if (bestEntry) {
          let newSection: "overview" | "agent" | "trends" | null = null;
          if (bestEntry.target === overviewRef.current) newSection = "overview";
          else if (bestEntry.target === agentRef.current) newSection = "agent";
          else if (bestEntry.target === trendsRef.current) newSection = "trends";

          if (newSection && newSection !== activeSection) {
            setActiveSection(newSection);
          }
        }
      }, 150); // 150ms debounce
    };

    const io = new IntersectionObserver(handler, options);
    if (overviewRef.current) io.observe(overviewRef.current);
    if (agentRef.current) io.observe(agentRef.current);
    if (trendsRef.current) io.observe(trendsRef.current);

    return () => {
      clearTimeout(timeoutId);
      io.disconnect();
    };
  }, [activeSection]);

  const handleAccordionValueChange = (
    section: "overview" | "agent" | "trends",
    setter: React.Dispatch<React.SetStateAction<string | undefined>>
  ) => (value: string | undefined) => {
    // Suppress IO-driven activeSection updates during accordion animation/layout shift
    suppressIoUntilRef.current = Date.now() + 500;
    setter(value);
    // Only set active section when expanding, keep as-is on collapse
    if (value) setActiveSection(section);
  };

  const getBtnClass = (key: "overview" | "agent" | "trends") =>
    activeSection === key
      ? "bg-[#00FEC1] text-black rounded-sm hover:bg-[#00FEC1] dark:hover:bg-[#00FEC1]"
      : "bg-white text-primary rounded-sm border border-input hover:bg-[#00FEC1] hover:text-black hover:border-transparent dark:bg-[#0C3451] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:text-black";

  const handleNavigateToSection = (section: "overview" | "agent" | "trends", ref: React.RefObject<HTMLDivElement>) => {
    // Expand the target accordion section
    if (section === "overview") {
      setOverviewOpen("call-detail");
    } else if (section === "agent") {
      setAgentOpen("call-detail");
    } else if (section === "trends") {
      setTrendsOpen("call-detail");
    }

    setActiveSection(section);

    // Small delay to allow accordion to expand before scrolling
    setTimeout(() => {
      scrollToSection(ref);
    }, 100);
  };

  const NavButtons: React.FC<{ headerSection: "overview" | "agent" | "trends" }> = ({ headerSection }) => (
    <div className="ml-auto flex items-center gap-2">
      {canViewCallQueue && (
        <span
          role="button"
          tabIndex={0}
          className={`${activeSection === "overview" && headerSection === "overview" ? getBtnClass("overview") : "bg-white text-primary rounded-sm border border-input hover:bg-[#00FEC1] hover:text-black hover:border-transparent dark:bg-[#0C3451] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:text-black"} no-underline px-3 py-1.5 cursor-pointer`}
          onClick={(e) => { e.stopPropagation(); handleNavigateToSection("overview", overviewRef); }}
          onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleNavigateToSection("overview", overviewRef); } }}
        >
          Call Queue Insight
        </span>
      )}
      <span
        role="button"
        tabIndex={0}
        className={`${activeSection === "agent" && headerSection === "agent" ? getBtnClass("agent") : "bg-white text-primary rounded-sm border border-input hover:bg-[#00FEC1] hover:text-black hover:border-transparent dark:bg-[#0C3451] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:text-black"} no-underline px-3 py-1.5 cursor-pointer`}
        onClick={(e) => { e.stopPropagation(); handleNavigateToSection("agent", agentRef); }}
        onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleNavigateToSection("agent", agentRef); } }}
      >
        Direct Call Insight
      </span>
      <span
        role="button"
        tabIndex={0}
        className={`${activeSection === "trends" && headerSection === "trends" ? getBtnClass("trends") : "bg-white text-primary rounded-sm border border-input hover:bg-[#00FEC1] hover:text-black hover:border-transparent dark:bg-[#0C3451] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:text-black"} no-underline px-3 py-1.5 cursor-pointer`}
        onClick={(e) => { e.stopPropagation(); handleNavigateToSection("trends", trendsRef); }}
        onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleNavigateToSection("trends", trendsRef); } }}
      >
        Trends
      </span>
    </div>
  );

  if (!isGeneralMode) return null;

  return (
    <div className="w-full">
      {canViewCallQueue && (
        <div ref={overviewRef}>
          <Accordion type="single" value={overviewOpen} onValueChange={handleAccordionValueChange("overview", setOverviewOpen)}>
            <AccordionItem value="call-detail">
              <AccordionTrigger noUnderline noIcon className="text-sm md:text-sm font-bold px-2 py-2 bg-transparent">
                <div className="w-full flex items-center gap-2">
                  <span className="underline ml-2" style={{ textDecorationColor: '#00FEC1', textDecorationThickness: '2px' }}>Call Queue Insight</span>
                  <NavButtons headerSection="overview" />
                </div>
              </AccordionTrigger>
              <AccordionContent className="p-2 bg-transparent">
                <div className="grid grid-cols-1 gap-4">
                  <CallOverviewSection isGeneralMode={true} />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}

      <div className="mt-4" ref={agentRef}>
        <Accordion type="single" value={agentOpen} onValueChange={handleAccordionValueChange("agent", setAgentOpen)}>
          <AccordionItem value="call-detail">
            <AccordionTrigger noUnderline noIcon className="text-sm md:text-sm font-bold px-2 py-2 bg-transparent">
              <div className="w-full flex items-center gap-2">
                <span className="underline ml-2" style={{ textDecorationColor: '#00FEC1', textDecorationThickness: '2px' }}>Direct Call Insight</span>
                <NavButtons headerSection="agent" />
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-2 bg-transparent">
              <div className="grid grid-cols-1 gap-4">
                <InstantConnectInsightSection isGeneralMode={true} />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <div className="mt-4" ref={trendsRef}>
        <Accordion type="single" value={trendsOpen} onValueChange={handleAccordionValueChange("trends", setTrendsOpen)}>
          <AccordionItem value="call-detail">
            <AccordionTrigger noUnderline noIcon className="text-sm md:text-sm font-bold px-2 py-2 bg-transparent">
              <div className="w-full flex items-center gap-2">
                <span className="underline ml-2" style={{ textDecorationColor: '#00FEC1', textDecorationThickness: '2px' }}>Trends</span>
                <NavButtons headerSection="trends" />
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-2 bg-transparent">
              <div className="grid grid-cols-1 gap-4">
                <TrendsSection isGeneralMode={true} />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};

export default CallsTab;
